function isVisibleInViewport(elm) {
  var rect = elm.getBoundingClientRect();
  var viewHeight = Math.max(document.documentElement.clientHeight, window.innerHeight);
  return !(rect.bottom < 0 || rect.top - viewHeight >= 0);
}

class ProductGalleryNextButton extends HTMLElement {
  constructor() {
    super();

    this.observer = null;
    this.abortController = new AbortController();
  }

  get galleryItems() {
    return this.gallery?.querySelectorAll('.product-gallery__media:not(.md\\:hidden)') || [];
  }

  async connectedCallback() {
    await new Promise((resolve) => setTimeout(resolve, 0));

    this.button = this.querySelector('button');
    this.gallery = this.closest('.product').querySelector('.product-gallery__image-list');

    this.galleryIntersectionObserver();
    this.initListeners();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.abortController.abort();
  }

  initListeners() {
    this.button.addEventListener('click', this.scrollNextPosition.bind(this), {
      signal: this.abortController.signal,
    });
  }

  async scrollNextPosition(e) {
    e.preventDefault();
    const lastVisibleIndex = Array.from(this.galleryItems).reduce((lastIndex, item, index) => {
      return isVisibleInViewport(item) ? index : lastIndex;
    }, -1);

    if (lastVisibleIndex !== -1) {
      const nextIndex = Math.min(lastVisibleIndex + 1, this.galleryItems.length - 1);
      const nextItem = this.galleryItems[nextIndex];
      nextItem.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  galleryIntersectionObserver() {
    const observerOptions = {
      root: null,
      rootMargin: `-${window.innerHeight * 1.5}px 0px 0px 0px`,
      threshold: 0,
    };

    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        this.button.style.opacity = entry.isIntersecting ? 1 : 0;
      });
    };

    this.observer = new IntersectionObserver(observerCallback, observerOptions);
    this.observer.observe(this.gallery);
  }
}

customElements.define('product-gallery-next-button', ProductGalleryNextButton);

window.quickShopHtmls = {};
class ProductCardQuickShop extends HTMLElement {
  constructor() {
    super();

    this.productSectionId = 'main-product--quick-shop';
    this.isRendered = false;
    this.observer = null;
    this.abortController = new AbortController();
  }

  async connectedCallback() {
    await new Promise((resolve) => setTimeout(resolve, 0));

    this.productHandle = this.getAttribute('data-product-handle');
    this.variantId = this.getAttribute('data-variant-id');
    this.productCard = this.closest('product-card');
    this.observeProductCard();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.abortController.abort();
  }

  initProductCardListeners() {
    this.productCard.addEventListener('mouseenter', this.handleMouseEnter.bind(this), {
      signal: this.abortController.signal,
    });
    this.productCard.addEventListener('mouseleave', this.handleMouseLeave.bind(this), {
      signal: this.abortController.signal,
    });
    this.productCard.addEventListener('click', this.handleProductCardClick.bind(this), {
      signal: this.abortController.signal,
    });
  }

  handleMouseEnter() {
    if (this.isRendered) return;

    const height = this.productCard.offsetHeight;
    this.productCard.style.minHeight = `${height}px`;
    this.productCard.classList.add('product-card--hovered');
    this.renderProductQuickShop(this.productHandle);
    this.enableFixScrollCarousel(true);
  }

  handleMouseLeave() {
    this.productCard.style.minHeight = '';
    this.productCard.classList.remove('product-card--hovered');
    this.triggerClickSwatch(this.variantId);
    this.innerHTML = '';
    this.isRendered = false;
    this.enableFixScrollCarousel(false);
  }

  enableFixScrollCarousel(apply) {
    const scrollCarousel = this.productCard.closest('scroll-carousel');
    const productList = this.productCard.closest('product-list');
    if (scrollCarousel && productList) {
      scrollCarousel.style.position = apply ? 'relative' : '';
      scrollCarousel.style.left = apply ? `-${scrollCarousel.scrollLeft}px` : '';
      scrollCarousel.style.overflow = apply ? 'visible' : '';
      productList.style.overflowX = apply ? 'clip' : '';
    }
  }

  async handleProductCardClick(e) {
    if (e.target.closest('.variant-picker__option-values')) {
      const input = e.target.closest('input');
      if (input) this.triggerClickSwatch(input.value);
    }
  }
  triggerClickSwatch(id) {
    const colorSwatchInput = this.productCard.querySelector(
      `.product-card__info input[value="${id}"], .product-card__info input[data-variant-id="${id}"]`
    );
    colorSwatchInput?.click();
  }

  observeProductCard() {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.25,
    };

    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.fetchProductQuickShop(this.productHandle);
          this.initProductCardListeners();
          this.observer.disconnect();
        }
      });
    };

    this.observer = new IntersectionObserver(observerCallback, observerOptions);
    this.observer.observe(this.productCard);
  }

  async fetchProductQuickShop(productHandle, returnHtmlParser = false) {
    if (window.quickShopHtmls[productHandle]) {
      return returnHtmlParser
        ? new DOMParser()
            .parseFromString(window.quickShopHtmls[productHandle], 'text/html')
            .querySelector('product-rerender')
        : null;
    }

    const url = new URL(`${Shopify.routes.root}products/${productHandle}`, window.location.origin);
    url.searchParams.append('variant', this.variantId);
    url.searchParams.append('section_id', this.productSectionId);
    url.searchParams.append('context', 'quick_shop');

    const response = await fetch(url);
    const responseHtml = await response.text();
    const newContent = new DOMParser().parseFromString(responseHtml, 'text/html');
    const quickShopElement = newContent.querySelector('product-rerender');
    window.quickShopHtmls[productHandle] = quickShopElement.outerHTML;

    return returnHtmlParser ? quickShopElement : null;
  }

  async renderProductQuickShop(productHandle) {
    const quickShopHtml = await this.fetchProductQuickShop(productHandle, true);

    this.innerHTML = quickShopHtml.outerHTML;
    this.isRendered = true;
    this.productCard.classList.add('product-card--quick-shop-rendered');
  }
}

customElements.define('product-card-quick-shop', ProductCardQuickShop);
